/**
 * Procedural ornate frames matching the Dark Fantasy UI kit look.
 * Pixel-ish metal/gold chrome — test styling, not final sliced art.
 */

import Phaser from 'phaser';
import { UI } from './theme';

/** Ornate rectangular panel: iron fill + double gold trim + corner pips. */
export function drawOrnateRect(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  options: {
    fill?: number;
    fillAlpha?: number;
    trim?: number;
    accent?: number;
    active?: boolean;
  } = {},
): void {
  const fill = options.fill ?? UI.bgPanel;
  const fillAlpha = options.fillAlpha ?? 1;
  const trim = options.trim ?? UI.gold;
  const accent = options.accent ?? UI.iron;
  const active = options.active ?? false;

  g.fillStyle(fill, fillAlpha);
  g.fillRoundedRect(x, y, w, h, 4);

  // Outer iron rim
  g.lineStyle(3, accent, 1);
  g.strokeRoundedRect(x + 1, y + 1, w - 2, h - 2, 4);

  // Gold trim
  g.lineStyle(active ? 3 : 2, active ? UI.goldBright : trim, active ? 1 : 0.9);
  g.strokeRoundedRect(x + 3, y + 3, w - 6, h - 6, 3);

  // Corner pips
  const pip = active ? UI.goldBright : trim;
  g.fillStyle(pip, 1);
  const s = 3;
  g.fillRect(x + 5, y + 5, s, s);
  g.fillRect(x + w - 5 - s, y + 5, s, s);
  g.fillRect(x + 5, y + h - 5 - s, s, s);
  g.fillRect(x + w - 5 - s, y + h - 5 - s, s, s);
}

/** Recessed slot (equipment / inventory empty). */
export function drawSlotRect(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  state: 'empty' | 'normal' | 'equipped' | 'disabled' = 'empty',
): void {
  const fills: Record<typeof state, number> = {
    empty: UI.bgRecess,
    normal: UI.bgStone,
    equipped: UI.bgPanel,
    disabled: 0x1a1a1a,
  };
  const trims: Record<typeof state, number> = {
    empty: UI.iron,
    normal: UI.ironLight,
    equipped: UI.goldBright,
    disabled: 0x2a2a2a,
  };
  g.fillStyle(fills[state], state === 'disabled' ? 0.45 : 1);
  g.fillRoundedRect(x, y, w, h, 3);
  g.lineStyle(state === 'equipped' ? 2 : 1, trims[state], 1);
  g.strokeRoundedRect(x + 1, y + 1, w - 2, h - 2, 3);
  if (state === 'empty') {
    g.lineStyle(1, UI.iron, 0.5);
    // dashed feel via corner ticks
    g.strokeRect(x + 4, y + 4, w - 8, h - 8);
  }
}

/** Circular talent node chrome. */
export function drawTalentNode(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  r: number,
  state: 'locked' | 'unlocked' | 'available',
): void {
  const fill =
    state === 'locked' ? UI.bgRecess : state === 'unlocked' ? UI.bgStone : UI.bgPanel;
  const trim =
    state === 'locked'
      ? UI.iron
      : state === 'unlocked'
        ? UI.cyan
        : UI.goldBright;
  g.fillStyle(fill, 1);
  g.fillCircle(cx, cy, r);
  g.lineStyle(state === 'available' ? 3 : 2, trim, 1);
  g.strokeCircle(cx, cy, r - 1);
  if (state === 'locked') {
    g.fillStyle(UI.ironLight, 0.8);
    g.fillCircle(cx, cy, 4);
  }
}

/** Slim progress bar track + fill (kit-style framed). */
export function drawProgressBar(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  ratio: number,
  fillColor = UI.gold,
): void {
  g.fillStyle(UI.bgRecess, 1);
  g.fillRoundedRect(x, y, w, h, 2);
  g.lineStyle(1, UI.ironLight, 1);
  g.strokeRoundedRect(x, y, w, h, 2);
  const fw = Math.max(2, Math.floor((w - 4) * Phaser.Math.Clamp(ratio, 0, 1)));
  g.fillStyle(fillColor, 1);
  g.fillRoundedRect(x + 2, y + 2, fw, h - 4, 1);
}

/** Simple pixel-ish icon glyphs drawn into a graphics object (centered). */
export function drawIconGlyph(
  g: Phaser.GameObjects.Graphics,
  kind:
    | 'helmet'
    | 'tree'
    | 'house'
    | 'scroll'
    | 'gear'
    | 'gate'
    | 'train'
    | 'anvil'
    | 'mug'
    | 'star'
    | 'coin'
    | 'mat',
  cx: number,
  cy: number,
  color = 0xe8e0f0,
): void {
  g.fillStyle(color, 1);
  g.lineStyle(2, color, 1);
  switch (kind) {
    case 'helmet':
      // Crested helm silhouette
      g.fillRect(cx - 9, cy - 2, 18, 10);
      g.fillTriangle(cx - 9, cy - 2, cx + 9, cy - 2, cx, cy - 12);
      g.fillRect(cx - 2, cy - 14, 4, 6);
      g.fillStyle(UI.bgPanel, 1);
      g.fillRect(cx - 6, cy + 1, 12, 4);
      break;
    case 'tree':
      g.fillCircle(cx, cy - 4, 7);
      g.fillRect(cx - 2, cy + 2, 4, 8);
      g.fillCircle(cx - 6, cy, 4);
      g.fillCircle(cx + 6, cy, 4);
      break;
    case 'house':
      g.fillTriangle(cx, cy - 10, cx - 12, cy, cx + 12, cy);
      g.fillRect(cx - 9, cy, 18, 10);
      g.fillStyle(UI.bgRecess, 1);
      g.fillRect(cx - 3, cy + 4, 6, 6);
      break;
    case 'scroll':
      g.fillRoundedRect(cx - 8, cy - 10, 16, 20, 3);
      g.fillStyle(UI.bgRecess, 1);
      g.fillRect(cx - 5, cy - 5, 10, 2);
      g.fillRect(cx - 5, cy, 10, 2);
      g.fillRect(cx - 5, cy + 5, 8, 2);
      break;
    case 'gear':
      g.fillCircle(cx, cy, 8);
      g.fillStyle(UI.bgPanel, 1);
      g.fillCircle(cx, cy, 3);
      g.fillStyle(color, 1);
      for (let i = 0; i < 4; i += 1) {
        const a = (i * Math.PI) / 2;
        g.fillRect(cx + Math.cos(a) * 8 - 2, cy + Math.sin(a) * 8 - 2, 4, 4);
      }
      break;
    case 'gate':
      g.fillRect(cx - 10, cy - 10, 20, 20);
      g.fillStyle(UI.bgRecess, 1);
      g.fillRect(cx - 5, cy - 2, 10, 12);
      g.fillStyle(color, 1);
      g.fillRect(cx - 12, cy - 12, 6, 6);
      g.fillRect(cx + 6, cy - 12, 6, 6);
      break;
    case 'train':
      g.fillRect(cx - 10, cy - 2, 20, 6);
      g.fillCircle(cx - 8, cy + 6, 4);
      g.fillCircle(cx + 8, cy + 6, 4);
      g.fillRect(cx - 2, cy - 8, 4, 8);
      break;
    case 'anvil':
      g.fillRect(cx - 12, cy - 2, 24, 6);
      g.fillRect(cx - 6, cy + 4, 12, 8);
      g.fillRect(cx - 4, cy - 8, 4, 8);
      break;
    case 'mug':
      g.fillRect(cx - 7, cy - 8, 12, 16);
      g.lineStyle(2, color, 1);
      g.strokeCircle(cx + 8, cy, 5);
      break;
    case 'star':
      g.fillTriangle(cx, cy - 10, cx - 4, cy - 2, cx + 4, cy - 2);
      g.fillTriangle(cx, cy + 10, cx - 4, cy + 2, cx + 4, cy + 2);
      g.fillTriangle(cx - 10, cy, cx - 2, cy - 4, cx - 2, cy + 4);
      g.fillTriangle(cx + 10, cy, cx + 2, cy - 4, cx + 2, cy + 4);
      break;
    case 'coin':
      g.fillCircle(cx, cy, 7);
      g.fillStyle(UI.goldDim, 1);
      g.fillCircle(cx, cy, 4);
      break;
    case 'mat':
      g.fillStyle(UI.amethyst, 1);
      g.fillTriangle(cx, cy - 8, cx - 7, cy + 6, cx + 7, cy + 6);
      break;
    default:
      break;
  }
}
